import { FormProps } from "app/core/components/Form"
import { Blockgroup, BlockgroupOnScenario } from "db"
import { Field, Form, Formik, useField, useFormikContext } from "formik"
import React, { useContext, useEffect, useState } from "react"
import { z } from "zod"
import { UpdateBlockgroupOnScenario } from "app/validations"
import { styledButton2 } from "components/elements"
export { FORM_ERROR } from "app/core/components/Form"
import Help from "components/help"
import getScenario from "app/scenarios/queries/getScenario"
import { ScenarioBuilderSlider } from "components/scenario_builder_slider"
import { getAreasOnBlockgroup } from "app/utils/scenario_utils"
import {
  getAcresNeeded,
  approxTreeCount,
  calculateTreeEquityScoreIncrease,
  calculateScenarioTotalTreeCanopySqft,
} from "app/utils/scenario_math"
import { squareFeetToAcres } from "app/utils/conversion_utils"
import { DEFAULT_TARGET_INCREASE } from "app/constants"
import { TreesFieldProps } from "../types"
import { CityContext } from "app/features/regional-map/regional-map.state"
import { validateZodSchema } from "blitz"

type GetScenario = Awaited<ReturnType<typeof getScenario>>

function TreesField({
  blockgroup,
  sliderLastTouched,
  setSliderLastTouched,
  ...props
}: TreesFieldProps) {
  const {
    values: { treeEquityScoreTarget },
    setFieldValue,
  } = useFormikContext<z.infer<typeof UpdateBlockgroupOnScenario>>()

  const [field] = useField(props)

  useEffect(() => {
    if (sliderLastTouched)
      setFieldValue(props.name, approxTreeCount(getAcresNeeded(blockgroup, treeEquityScoreTarget)))
    setSliderLastTouched(false)
  }, [
    treeEquityScoreTarget,
    sliderLastTouched,
    setFieldValue,
    props.name,
    blockgroup,
    setSliderLastTouched,
  ])

  return (
    <>
      <input {...props} {...field} value={field.value ?? ""} />
    </>
  )
}

export function BlockgroupOnScenarioForm({
  blockgroup,
  scenario,
  blockgroupOnScenario,
  onSubmit,
  onCancel,
}: FormProps<typeof UpdateBlockgroupOnScenario> & {
  blockgroup: Blockgroup
  scenario: GetScenario
  blockgroupOnScenario: BlockgroupOnScenario | null
  submitText: string
  onCancel: () => void
}) {
  const city = useContext(CityContext)!
  const schema = UpdateBlockgroupOnScenario
  const [formError, setFormError] = useState<string | null>(null)
  const [sliderLastTouched, setSliderLastTouched] = useState(false)
  const areas = getAreasOnBlockgroup(scenario, blockgroup)
  const totalTreeCanopyAdded: number = squareFeetToAcres(
    calculateScenarioTotalTreeCanopySqft(areas)
  )
  const maxTargetReached = blockgroup.tree_equity_score === 100
  const improvedTarget: number =
    areas.length && blockgroupOnScenario
      ? calculateTreeEquityScoreIncrease(blockgroup, totalTreeCanopyAdded)
      : 0

  const minTrees = 0
  const maxTrees = approxTreeCount(getAcresNeeded(blockgroup, 100))
  const defaultTrees = blockgroupOnScenario
    ? approxTreeCount(getAcresNeeded(blockgroup, blockgroupOnScenario.treeEquityScoreTarget))
    : approxTreeCount(
        getAcresNeeded(blockgroup, blockgroup.tree_equity_score + DEFAULT_TARGET_INCREASE)
      )

  return (
    <Formik<z.infer<typeof UpdateBlockgroupOnScenario>>
      enableReinitialize
      initialValues={
        blockgroupOnScenario
          ? {
              ...blockgroupOnScenario,
              trees: defaultTrees,
            }
          : {
              // default target should be +5, but should not exceed 100
              treeEquityScoreTarget: Math.min(
                100,
                blockgroup.tree_equity_score + DEFAULT_TARGET_INCREASE
              ),
              scenarioId: scenario.id,
              blockgroupId: blockgroup.id,
              trees: defaultTrees,
            }
      }
      validate={async (val) => {
        return await validateZodSchema(schema)(val)
      }}
      onSubmit={async (values, { setErrors }) => {
        try {
          const { FORM_ERROR, ...otherErrors } = (await onSubmit(values)) || {}

          if (FORM_ERROR) {
            setFormError(FORM_ERROR)
          }

          if (Object.keys(otherErrors).length > 0) {
            setErrors(otherErrors)
          }
        } catch {
          setFormError("Failed to save. Please try again.")
        }
      }}
    >
      {(props) => {
        const { values, touched } = props
        return (
          <Form>
            <div>
              <div className="space-y-2 bg-gray-100 p-2 py-4">
                <div className="text-center text-caption font-bold text-gray-700">
                  Choose a {city.locale && city.locale === "en-CA" ? "census tract" : "block group"}{" "}
                  Tree Equity Score target{" "}
                  <Help className="ml-2 inline-block h-4 w-4 text-brand-green">
                    <p>
                      Achieving a Tree Equity Score of 100 can be a multi-year project. Set an
                      interim Target Score that fits your needs. Make progress toward your target by
                      allocating trees at the parcel level--this will update your Improved Score.
                    </p>
                  </Help>
                </div>
                {!touched.treeEquityScoreTarget && !blockgroupOnScenario && !maxTargetReached && (
                  <div className="pl-2 text-annotation text-gray-600">
                    A default target of +5 has been set for this{" "}
                    {city.locale && city.locale === "en-CA" ? "census tract" : "block group"}'s Tree
                    Equity Score. Adjust it below if needed.
                  </div>
                )}
                <div className="flex flex-row pt-10">
                  <div className="mr-2">
                    <p className="-mb-1 text-center text-[0.65rem] font-bold uppercase text-gray-500">
                      starting
                    </p>
                    <p className="text-center text-caption font-bold uppercase text-gray-500">
                      {blockgroup.tree_equity_score}
                    </p>
                  </div>
                  <Field
                    min={blockgroup.tree_equity_score}
                    max={100}
                    maxTargetReached={maxTargetReached}
                    component={ScenarioBuilderSlider}
                    className="w-3/4"
                    name="treeEquityScoreTarget"
                    defaultValue={blockgroup.tree_equity_score + DEFAULT_TARGET_INCREASE}
                    improvedTarget={improvedTarget}
                    trees={values.trees}
                    blockgroup={blockgroup}
                    setSliderLastTouched={setSliderLastTouched}
                  />
                  <div className="ml-2">
                    <p className="-mb-1 text-center text-annotation font-bold uppercase text-gray-500">
                      max
                    </p>
                    <p className="text-center text-caption font-bold uppercase text-gray-500">
                      100
                    </p>
                  </div>
                </div>
                {blockgroup.tree_equity_score === 100 ? (
                  <div className="m-auto w-3/4 max-w-[300px] pl-2 text-center text-annotation text-red-500">
                    <p className="font-bold">
                      This {city.locale && city.locale === "en-CA" ? "census tract" : "block group"}{" "}
                      already has a score of 100.
                    </p>
                    <p>
                      Try selecting a lower-scoring{" "}
                      {city.locale && city.locale === "en-CA" ? "census tract" : "block group"} with
                      greater need.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-row items-center pt-6">
                    <p className="pl-2 text-annotation text-gray-500">
                      approx. number of trees targeted
                    </p>
                    <TreesField
                      className="h-6 w-16 rounded-sm border-gray-300 p-1 text-right text-annotation font-bold tabular-nums text-brand-green"
                      name="trees"
                      min={minTrees}
                      max={maxTrees}
                      type="number"
                      blockgroup={blockgroup}
                      sliderLastTouched={sliderLastTouched}
                      setSliderLastTouched={setSliderLastTouched}
                    />
                    <Help className="ml-2 block h-4 w-4 text-brand-green">
                      <p>
                        Setting a Tree Equity Score target approximates the minimum number of
                        medium-sized trees (
                        {city.locale && city.locale === "en-CA" ? "56sq-m" : "600sq-ft"}) needed to
                        shift the starting score to the target score. You can also customize the
                        number of trees when setting a{" "}
                        {city.locale && city.locale === "en-CA" ? "census tract" : "block group"}{" "}
                        target.
                      </p>
                    </Help>
                  </div>
                )}
              </div>
              <div className="flex justify-center gap-x-2 p-4">
                <button className={styledButton2({ variant: "primary" })} type="submit">
                  {blockgroupOnScenario ? "Save your updates" : "Add to scenario"}
                </button>
                <button
                  className={styledButton2({ variant: "outline" })}
                  type="button"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
              {formError && (
                <div role="alert" className="p-3 text-sm text-red-500">
                  {formError}
                </div>
              )}
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}
